import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useModuleSettings } from "@/hooks/useModuleSettings";

interface DemoModeContextType {
  isDemoMode: boolean;
  toggleDemoMode: () => void;
  setDemoMode: (on: boolean) => void;
  demoEnabled: boolean; // feature flag
}

const DemoModeContext = createContext<DemoModeContextType>({
  isDemoMode: false,
  toggleDemoMode: () => {},
  setDemoMode: () => {},
  demoEnabled: false,
});

export function DemoModeProvider({ children }: { children: ReactNode }) {
  const { isModuleEnabled } = useModuleSettings();
  const demoEnabled = isModuleEnabled("demo_trading");

  const [isDemoMode, setIsDemoMode] = useState(() => {
    try {
      return localStorage.getItem("acex_demo_mode") === "true";
    } catch {
      return false;
    }
  });

  const setDemoMode = useCallback((on: boolean) => {
    setIsDemoMode(on);
    try { localStorage.setItem("acex_demo_mode", String(on)); } catch {}
  }, []);

  const toggleDemoMode = useCallback(() => {
    setDemoMode(!isDemoMode);
  }, [isDemoMode, setDemoMode]);

  // If module disabled, force real mode
  const effectiveDemo = demoEnabled && isDemoMode;

  return (
    <DemoModeContext.Provider value={{ isDemoMode: effectiveDemo, toggleDemoMode, setDemoMode, demoEnabled }}>
      {children}
    </DemoModeContext.Provider>
  );
}

export function useDemoMode() {
  return useContext(DemoModeContext);
}
